import { Component, EventEmitter, inject, OnInit, Output } from '@angular/core';
import { PrimeTemplate, TreeNode } from 'primeng/api';
import { TableModule } from 'primeng/table';
import { IndicatorStore } from '../../../stores/indicator/indicator.state';
import { WidgetStore } from '../../../stores/grid/widget.state';
import { Indicator } from '../../../models/indicator';
import { IndicatorDataStore } from '../../../stores/indicatpr-data/indicator-data.state';
import { GlobalsStore } from '../../../stores/globals.state';
import { IndicatorData } from '../../../models/indicator-data';
import { TreeTableModule } from 'primeng/treetable';
import { NgClass } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';

@Component({
  selector: 'app-indicator-list',
  standalone: true,
  imports: [
    PrimeTemplate,
    TableModule,
    TreeTableModule,
    NgClass,
    InputTextModule,
  ],
  templateUrl: './indicator-list.component.html',
  styleUrl: './indicator-list.component.scss',
})
export class IndicatorListComponent implements OnInit {
  @Output() selectIndicator = new EventEmitter<string>();
  readonly indicatorStore = inject(IndicatorStore);
  readonly dataStore = inject(IndicatorDataStore);
  readonly widgetStore = inject(WidgetStore);
  readonly globalStore = inject(GlobalsStore);

  typeId?: string;
  availableData: IndicatorData[] = [];
  indicatorIds: string[] = [];

  treeIndicators: TreeNode[] = [];
  indicators: Indicator[] = [];

  select(indicatorId: string) {
    this.selectIndicator.emit(indicatorId);
  }

  /**
   * Initializes the component after Angular has set up the inputs and outputs.
   * This lifecycle hook is called once during the component's initialization phase.
   *
   * The method performs the following tasks:
   * - Logs initial status updates.
   * - Invokes setAvailableData() to set up required available data.
   * - Calls setIndicators() to configure necessary indicators.
   * - Executes testTableList(), which may have a performance impact due to being potentially slow.
   * - Logs status updates after each operation for tracking the process flow.
   *
   * @return {void} No value is returned as this method is solely for initialization purposes.
   */
  ngOnInit(): void {
    console.log('list 1');
    this.setAvailableData();
    console.log('list 2');
    this.setIndicators();
    console.log('list 3');
    this.testTableList(); //<-- slow!
    console.log('list 4');
  }

  /**
   * Updates the availableData property by filtering data based on the selected data layer type
   * from the widget store. It also updates the indicator IDs and applies filter updates
   * to the indicator store.
   *
   * @return {void} This method does not return a value.
   */
  setAvailableData(): void {
    this.typeId = this.widgetStore.selected()?.widget?.dataLayerType;
    if (this.typeId) {
      this.availableData = this.dataStore.filterByDataLayerType(this.typeId);

      const indicatorIdsWithoutSets = this.availableData.map(
        item => item.indicator
      );
      this.indicatorIds = indicatorIdsWithoutSets.concat(
        this.indicatorStore.getSetIdsByIds(indicatorIdsWithoutSets)
      );

      this.indicatorStore.updateFilter({
        ...this.indicatorStore.filter(),
        ...{ ids: this.indicatorIds },
      });
    }
  }

  /**
   * Updates the indicators property based on the typeId and indicatorIds properties.
   * If typeId or indicatorIds are not set, or if indicatorIds is empty, the indicators property is reset to an empty array.
   * Otherwise, it uses the filtered items from the indicatorStore to set the indicators property.
   * @return {void} Does not return a value.
   */
  setIndicators(): void {
    if (!this.typeId || !this.indicatorIds || !this.indicatorIds.length) {
      this.indicators = [];
    } else {
      this.indicators = this.indicatorStore.filteredItems();
    }
  }

  /**
   * Processes and transforms the list of indicators into a hierarchical tree structure.
   *
   * @return {void} This method does not return any value.
   */
  private testTableList(): void {
    const groupedChildren = this.indicatorStore.groupedChildren();
    console.log('this.indicators num', this.indicators.length);
    this.treeIndicators = this.indicators.map((indicator: Indicator) => {
      if (indicator.isSet) {
        const children = (groupedChildren[indicator._id] || []).map(
          (child: Indicator) => ({
            data: { name: child.name, key: child._id, type: 'child' },
          })
        );

        return {
          data: {
            name: indicator.name,
            key: indicator._id,
            type: 'set',
          },
          expandedIcon: 'fas fa-ruler',
          collapsedIcon: 'fas fa-ruler-combined',
          children: children,
        };
      } else {
        return {
          data: {
            name: indicator.name,
            type: 'default',
            key: indicator._id,
          },
        };
      }
    });
  }

  /**
   * Handles the click event on a specific row in a tree structure.
   * Toggles the expanded state for set-type nodes and selects key for non-set nodes.
   * Updates table value after expansion for proper change detection.
   *
   * @param {TreeNode} rowNode - The node object of the clicked row.
   * @param {Event} event - The click event associated with the row click.
   * @return {void} This method does not return a value.
   */
  handleRowClick(rowNode: TreeNode, event: Event): void {
    console.log(rowNode);
    if (rowNode.data.type !== 'set') {
      this.select(rowNode.data.key);
    } else {
      event.stopPropagation();
      rowNode.expanded = !rowNode.expanded;
      this.updateTableValue(); // for change-detection - otherwise the expanded-value is ignored
    }
  }

  /**
   * Updates the table value by triggering change detection for tree indicators.
   * This function reassigns the `treeIndicators` property to a new array containing
   * the same elements, ensuring Angular's change detection mechanism is activated.
   *
   * @return {void} Does not return a value.
   */
  updateTableValue(): void {
    this.treeIndicators = [...this.treeIndicators]; // Trigger change detection
  }

  /**
   * Retrieves and casts the given target to an HTMLInputElement.
   *
   * @param {EventTarget | null} target - The event target to be cast as an HTMLInputElement.
   * @return {HTMLInputElement} The target cast as an HTMLInputElement.
   */
  getTarget(target: EventTarget | null): HTMLInputElement {
    return target as HTMLInputElement;
  }
}
